"use client";

import {
  m as motion,
  AnimatePresence,
  useReducedMotion,
  useInView,
} from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Visual signature della pagina /chi-sono (sezione "Vision").
 * Carosello di scene line-art in ciano sul tema "la 4ª dimensione = il tempo".
 *   0 - Trio impossibile: tesseract 4D + nastro di Möbius + triangolo di Penrose.
 *   1 - Spazio-tempo: griglia curvata (rubber-sheet GR) + massa + orbita.
 *   2 - Tempo: orologio con lancette che scorrono.
 *   3 - Cubismo: facce + ghost (più tempi insieme).
 *   4 - Escher: tassellatura interlock con onda di metamorfosi.
 * Navigazione: swipe/drag (touch), frecce + tastiera ←/→ (desktop), pallini.
 * Auto-avanza ogni 7s (pausa su hover). Si ferma fuori viewport e con
 * prefers-reduced-motion. Decorativa: aria-hidden sulle grafiche.
 */

const SCENES = [
  {
    id: "trio",
    caption: {
      it: "Oltre le 3 dimensioni",
      en: "Beyond 3 dimensions",
    },
  },
  {
    id: "spacetime",
    caption: { it: "Spazio-tempo · la curvatura", en: "Spacetime · curvature" },
  },
  {
    id: "clock",
    caption: { it: "Il tempo · che scorre", en: "Time · flowing" },
  },
  {
    id: "cubism",
    caption: {
      it: "Cubismo · più viste, un tempo",
      en: "Cubism · many views, one time",
    },
  },
] as const;

const STROKE = "hsl(var(--primary))";
const SURFACE = "hsl(var(--surface))";
const CX = 100;

/* ── tesseract 4D (parametrico: centro + scala) ────────────────── */
function Hypercube({
  cx = 100,
  cy = 100,
  scale = 150,
  animate,
}: {
  cx?: number;
  cy?: number;
  scale?: number;
  animate: boolean;
}) {
  const lineRefs = useRef<Array<SVGLineElement | null>>([]);
  const { vertices, edges } = useMemo(() => {
    const v: number[][] = [];
    for (let i = 0; i < 16; i++)
      v.push([i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1, i & 8 ? 1 : -1]);
    const e: Array<[number, number]> = [];
    for (let i = 0; i < 16; i++)
      for (let j = i + 1; j < 16; j++) {
        const d = i ^ j;
        if ((d & (d - 1)) === 0) e.push([i, j]);
      }
    return { vertices: v, edges: e };
  }, []);

  useEffect(() => {
    let raf = 0;
    let angle = 0;
    const project = (p: number[], a: number) => {
      let [x, y, z, w] = p;
      const cA = Math.cos(a),
        sA = Math.sin(a);
      [x, w] = [x * cA - w * sA, x * sA + w * cA];
      const b = a * 0.55;
      const cB = Math.cos(b),
        sB = Math.sin(b);
      [y, z] = [y * cB - z * sB, y * sB + z * cB];
      const k4 = 1 / (2.4 - w);
      x *= k4;
      y *= k4;
      z *= k4;
      const k3 = 1 / (3 - z);
      return { x: cx + x * scale * k3, y: cy + y * scale * k3, depth: k4 * k3 };
    };
    const draw = () => {
      const proj = vertices.map((p) => project(p, angle));
      for (let i = 0; i < edges.length; i++) {
        const ln = lineRefs.current[i];
        if (!ln) continue;
        const a = proj[edges[i][0]];
        const b = proj[edges[i][1]];
        ln.setAttribute("x1", a.x.toFixed(2));
        ln.setAttribute("y1", a.y.toFixed(2));
        ln.setAttribute("x2", b.x.toFixed(2));
        ln.setAttribute("y2", b.y.toFixed(2));
        const o = Math.min(1, Math.max(0.2, 0.2 + ((a.depth + b.depth) / 2 - 0.08) * 3.3));
        ln.setAttribute("opacity", o.toFixed(2));
      }
    };
    if (!animate) {
      draw();
      return;
    }
    const loop = () => {
      angle += 0.0065;
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [animate, vertices, edges, cx, cy, scale]);

  return (
    <g stroke={STROKE} strokeWidth={1.1} strokeLinecap="round" fill="none">
      {edges.map((_, i) => (
        <line key={i} ref={(el) => {lineRefs.current[i] = el;}} />
      ))}
    </g>
  );
}

/* ── nastro di Möbius (parametrico) ────────────────────────────── */
function Mobius({
  cx = 100,
  cy = 100,
  R = 40,
  hw = 16,
  animate,
}: {
  cx?: number;
  cy?: number;
  R?: number;
  hw?: number;
  animate: boolean;
}) {
  const { edgeA, edgeB, rungs, px, py } = useMemo(() => {
    const proj = (X: number, Y: number, Z: number): [number, number] => [
      cx + X,
      cy + Y * 0.5 - Z * 0.82,
    ];
    const edge = (w: number) => {
      let d = "";
      for (let k = 0; k <= 90; k++) {
        const t = (k / 90) * Math.PI * 2;
        const rad = R + w * hw * Math.cos(t / 2);
        const [x, y] = proj(rad * Math.cos(t), rad * Math.sin(t), w * hw * Math.sin(t / 2));
        d += (k === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1) + " ";
      }
      return d;
    };
    const rungs: Array<[number, number, number, number]> = [];
    for (let k = 0; k < 20; k++) {
      const t = (k / 20) * Math.PI * 2;
      const radP = R + hw * Math.cos(t / 2);
      const radM = R - hw * Math.cos(t / 2);
      const [x1, y1] = proj(radP * Math.cos(t), radP * Math.sin(t), hw * Math.sin(t / 2));
      const [x2, y2] = proj(radM * Math.cos(t), radM * Math.sin(t), -hw * Math.sin(t / 2));
      rungs.push([x1, y1, x2, y2]);
    }
    const px: number[] = [];
    const py: number[] = [];
    for (let k = 0; k <= 120; k++) {
      const t = (k / 120) * Math.PI * 4;
      const rad = R + hw * Math.cos(t / 2);
      const [x, y] = proj(rad * Math.cos(t), rad * Math.sin(t), hw * Math.sin(t / 2));
      px.push(x);
      py.push(y);
    }
    return { edgeA: edge(1), edgeB: edge(-1), rungs, px, py };
  }, [cx, cy, R, hw]);

  return (
    <>
      <g stroke={STROKE} fill="none">
        {rungs.map((r, i) => (
          <line key={i} x1={r[0]} y1={r[1]} x2={r[2]} y2={r[3]} strokeWidth={0.6} opacity={0.28} />
        ))}
        <path d={edgeA} strokeWidth={1.1} opacity={0.85} />
        <path d={edgeB} strokeWidth={1.1} opacity={0.5} />
      </g>
      <motion.circle
        r={3}
        fill={STROKE}
        cx={px[0]}
        cy={py[0]}
        animate={animate ? { cx: px, cy: py } : undefined}
        transition={animate ? { duration: 9, repeat: Infinity, ease: "linear" } : undefined}
      />
    </>
  );
}

/* ── triangolo di Penrose (tribar di cubi, proiezione isometrica) ─ */
function Penrose({
  cx = 100,
  cy = 100,
  scale = 11,
}: {
  cx?: number;
  cy?: number;
  scale?: number;
}) {
  const faces = useMemo(() => {
    const iso = (gx: number, gy: number, gz: number): [number, number] => {
      const sx = gx - gy;
      const sy = (gx + gy) * 0.5 - gz;
      return [-sy, sx]; // ruota +90° → apice in alto
    };
    const cubes: Array<[number, number, number]> = [];
    for (let x = 0; x <= 3; x++) cubes.push([x, 0, 0]); // lato sinistro
    for (let z = 1; z <= 3; z++) cubes.push([3, 0, z]); // base
    for (let y = 1; y <= 3; y++) cubes.push([3, y, 3]); // lato destro
    cubes.sort((a, b) => a[0] + a[1] + a[2] - (b[0] + b[1] + b[2])); // lontano→vicino
    const raw: Array<Array<[number, number]>> = [];
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    for (const [gx, gy, gz] of cubes) {
      const c = (dx: number, dy: number, dz: number) =>
        iso(gx + dx, gy + dy, gz + dz);
      const quads: Array<Array<[number, number]>> = [
        [c(0, 0, 1), c(1, 0, 1), c(1, 1, 1), c(0, 1, 1)], // top
        [c(1, 0, 0), c(1, 1, 0), c(1, 1, 1), c(1, 0, 1)], // lato
        [c(0, 1, 0), c(1, 1, 0), c(1, 1, 1), c(0, 1, 1)], // lato
      ];
      for (const q of quads) {
        for (const p of q) {
          if (p[0] < minX) minX = p[0];
          if (p[0] > maxX) maxX = p[0];
          if (p[1] < minY) minY = p[1];
          if (p[1] > maxY) maxY = p[1];
        }
        raw.push(q);
      }
    }
    const mx = (minX + maxX) / 2;
    const my = (minY + maxY) / 2;
    return raw.map((q) =>
      q
        .map(
          (p) =>
            `${(cx + (p[0] - mx) * scale).toFixed(1)},${(cy + (p[1] - my) * scale).toFixed(1)}`
        )
        .join(" ")
    );
  }, [cx, cy, scale]);

  return (
    <g stroke={STROKE} strokeWidth={1.1} strokeLinejoin="round">
      {faces.map((pts, i) => (
        <polygon key={i} points={pts} fill={SURFACE} />
      ))}
    </g>
  );
}

/* ── 0: trio impossibile ───────────────────────────────────────── */
function TrioScene({ animate }: { animate: boolean }) {
  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden>
      <motion.g
        animate={animate ? { opacity: [0.85, 1, 0.85] } : undefined}
        transition={animate ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : undefined}
      >
        <Penrose cx={100} cy={56} scale={11} />
      </motion.g>
      <Hypercube cx={60} cy={132} scale={74} animate={animate} />
      <Mobius cx={142} cy={130} R={22} hw={9} animate={animate} />
    </svg>
  );
}

/* ── 1: spazio-tempo (rubber-sheet GR + orbita) ────────────────── */
function SpacetimeScene({ animate }: { animate: boolean }) {
  const { rows, cols, mass, ox, oy } = useMemo(() => {
    const cx = 100,
      cy = 74,
      sX = 64,
      sZ = 24,
      sY = 40,
      depth = 1.3,
      r0 = 0.46,
      pk = 0.26;
    const project = (u: number, v: number): [number, number] => {
      const r2 = u * u + v * v;
      const well = -depth / (1 + r2 / (r0 * r0));
      const persp = 1 - v * pk;
      return [cx + u * sX * persp, cy - v * sZ - well * sY];
    };
    const N = 11,
      S = 16;
    const rows: string[] = [];
    const cols: string[] = [];
    for (let i = 0; i < N; i++) {
      const v = -1 + (2 * i) / (N - 1);
      let d = "";
      for (let j = 0; j <= S; j++) {
        const [x, y] = project(-1 + (2 * j) / S, v);
        d += (j === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1) + " ";
      }
      rows.push(d);
    }
    for (let j = 0; j < N; j++) {
      const u = -1 + (2 * j) / (N - 1);
      let d = "";
      for (let i = 0; i <= S; i++) {
        const [x, y] = project(u, -1 + (2 * i) / S);
        d += (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1) + " ";
      }
      cols.push(d);
    }
    const mass = project(0, 0);
    const ox: number[] = [];
    const oy: number[] = [];
    for (let k = 0; k <= 48; k++) {
      const a = (k / 48) * Math.PI * 2;
      const [x, y] = project(0.62 * Math.cos(a), 0.62 * Math.sin(a));
      ox.push(x);
      oy.push(y);
    }
    return { rows, cols, mass, ox, oy };
  }, []);

  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden>
      <g stroke={STROKE} fill="none" strokeWidth={0.6} opacity={0.42}>
        {rows.map((d, i) => (
          <path key={"r" + i} d={d} />
        ))}
        {cols.map((d, i) => (
          <path key={"c" + i} d={d} />
        ))}
      </g>
      <circle cx={mass[0]} cy={mass[1]} r={11} fill="none" stroke={STROKE} strokeWidth={0.7} opacity={0.35} />
      <motion.circle
        cx={mass[0]}
        cy={mass[1]}
        r={5.5}
        fill={STROKE}
        animate={animate ? { opacity: [0.75, 1, 0.75] } : undefined}
        transition={animate ? { duration: 3, repeat: Infinity, ease: "easeInOut" } : undefined}
      />
      <motion.circle
        r={3}
        fill={STROKE}
        cx={ox[0]}
        cy={oy[0]}
        animate={animate ? { cx: ox, cy: oy } : undefined}
        transition={animate ? { duration: 12, repeat: Infinity, ease: "linear" } : undefined}
      />
    </svg>
  );
}

/* ── 2: orologio (il tempo che scorre) ─────────────────────────── */
function ClockScene({ animate }: { animate: boolean }) {
  const ticks = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
        const major = i % 3 === 0;
        const r1 = major ? 58 : 63;
        const r2 = 70;
        return {
          x1: CX + Math.cos(a) * r1,
          y1: CX + Math.sin(a) * r1,
          x2: CX + Math.cos(a) * r2,
          y2: CX + Math.sin(a) * r2,
          major,
        };
      }),
    []
  );
  const handStyle = {
    transformBox: "view-box" as const,
    transformOrigin: "100px 100px",
  };
  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden>
      <circle cx={CX} cy={CX} r={70} fill="none" stroke={STROKE} strokeWidth={1} opacity={0.4} />
      <g stroke={STROKE} strokeLinecap="round">
        {ticks.map((t, i) => (
          <line
            key={i}
            x1={t.x1}
            y1={t.y1}
            x2={t.x2}
            y2={t.y2}
            strokeWidth={t.major ? 1.4 : 0.7}
            opacity={t.major ? 0.7 : 0.4}
          />
        ))}
      </g>
      <motion.line
        x1={CX}
        y1={CX}
        x2={CX}
        y2={CX - 34}
        stroke={STROKE}
        strokeWidth={2.2}
        strokeLinecap="round"
        style={handStyle}
        animate={animate ? { rotate: 360 } : undefined}
        transition={animate ? { duration: 90, repeat: Infinity, ease: "linear" } : undefined}
      />
      <motion.line
        x1={CX}
        y1={CX}
        x2={CX}
        y2={CX - 52}
        stroke={STROKE}
        strokeWidth={1.4}
        strokeLinecap="round"
        opacity={0.85}
        style={handStyle}
        animate={animate ? { rotate: 360 } : undefined}
        transition={animate ? { duration: 22, repeat: Infinity, ease: "linear" } : undefined}
      />
      <motion.line
        x1={CX}
        y1={CX + 9}
        x2={CX}
        y2={CX - 60}
        stroke={STROKE}
        strokeWidth={0.8}
        strokeLinecap="round"
        opacity={0.6}
        style={handStyle}
        animate={animate ? { rotate: 360 } : undefined}
        transition={animate ? { duration: 5, repeat: Infinity, ease: "linear" } : undefined}
      />
      <circle cx={CX} cy={CX} r={2.6} fill={STROKE} />
    </svg>
  );
}

/* ── 3: cubismo ────────────────────────────────────────────────── */
type Facet = { points: string; dx: number; dy: number; o: number; ghost?: boolean };
const FACETS: Facet[] = [
  { points: "100,28 62,74 100,106", dx: 3, dy: -2, o: 0.85 },
  { points: "100,28 138,74 100,106", dx: -3, dy: -2, o: 0.7 },
  { points: "62,74 50,112 100,106", dx: -4, dy: 1, o: 0.78 },
  { points: "138,74 150,112 100,106", dx: 4, dy: 1, o: 0.62 },
  { points: "62,74 138,74 100,106", dx: 0, dy: -3, o: 0.5 },
  { points: "50,112 66,152 100,106", dx: -3, dy: 4, o: 0.82 },
  { points: "150,112 134,152 100,106", dx: 3, dy: 4, o: 0.55 },
  { points: "66,152 100,172 100,106", dx: -1, dy: 5, o: 0.9 },
  { points: "134,152 100,172 100,106", dx: 1, dy: 5, o: 0.66 },
  { points: "100,28 62,74 100,106", dx: 11, dy: -6, o: 0.22, ghost: true },
  { points: "50,112 66,152 100,106", dx: -12, dy: 7, o: 0.2, ghost: true },
  { points: "138,74 150,112 100,106", dx: 12, dy: 5, o: 0.2, ghost: true },
];

function CubismScene({ animate }: { animate: boolean }) {
  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden>
      <g stroke={STROKE} fill="none" strokeWidth={1} strokeLinejoin="round">
        {FACETS.map((f, i) => (
          <motion.polygon
            key={i}
            points={f.points}
            strokeDasharray={f.ghost ? "3 3" : undefined}
            opacity={f.o}
            animate={
              animate
                ? { x: [0, f.dx, 0], y: [0, f.dy, 0], opacity: [f.o * 0.45, f.o, f.o * 0.45] }
                : undefined
            }
            transition={
              animate
                ? { duration: f.ghost ? 6 : 5, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }
                : undefined
            }
          />
        ))}
      </g>
    </svg>
  );
}

function Chevron({ dir }: { dir: -1 | 1 }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d={dir === 1 ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FourthDimensionVisual({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { amount: 0.3 });
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const playing = inView && !reduce;
  const go = (dir: number) =>
    setIndex((i) => (i + dir + SCENES.length) % SCENES.length);

  useEffect(() => {
    if (!playing || paused) return;
    const id = window.setTimeout(() => go(1), 7000);
    return () => window.clearTimeout(id);
  }, [index, playing, paused]);

  const scene = SCENES[index];

  return (
    <div ref={containerRef} className="relative mx-auto w-full max-w-md">
      <div className="pointer-events-none absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl" />
      <div
        role="group"
        aria-roledescription="carousel"
        aria-label={isItalian ? "La quarta dimensione" : "The fourth dimension"}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") {
            e.preventDefault();
            go(1);
          } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            go(-1);
          }
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="surface-card relative aspect-square overflow-hidden p-6 outline-none ring-primary/50 focus-visible:ring-2 md:p-8"
      >
        <div className="pattern-dots pointer-events-none absolute inset-0 opacity-40" />

        <span className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 border-l border-t border-primary/30" />
        <span className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 border-r border-t border-primary/30" />
        <span className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 border-b border-l border-primary/30" />
        <span className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 border-b border-r border-primary/30" />

        <div className="relative flex h-full flex-col">
          <motion.div
            className="relative min-h-0 flex-1 cursor-grab touch-pan-y active:cursor-grabbing"
            drag="x"
            dragSnapToOrigin
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragStart={() => setPaused(true)}
            onDragEnd={(_, info) => {
              if (info.offset.x < -40) go(1);
              else if (info.offset.x > 40) go(-1);
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={scene.id}
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.06 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="h-[82%] w-[82%]">
                  {scene.id === "trio" && <TrioScene animate={playing} />}
                  {scene.id === "spacetime" && <SpacetimeScene animate={playing} />}
                  {scene.id === "clock" && <ClockScene animate={playing} />}
                  {scene.id === "cubism" && <CubismScene animate={playing} />}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <button
            type="button"
            onClick={() => go(-1)}
            aria-label={isItalian ? "Precedente" : "Previous"}
            className="absolute left-2 top-1/2 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--border))] bg-surface/70 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary md:flex"
          >
            <Chevron dir={-1} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label={isItalian ? "Successiva" : "Next"}
            className="absolute right-2 top-1/2 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--border))] bg-surface/70 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary md:flex"
          >
            <Chevron dir={1} />
          </button>

          <div className="relative mt-2 flex items-center justify-between gap-3">
            <AnimatePresence mode="wait">
              <motion.span
                key={scene.id}
                className="font-mono text-xs text-muted-foreground"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.4 }}
              >
                {isItalian ? scene.caption.it : scene.caption.en}
              </motion.span>
            </AnimatePresence>
            <div className="flex shrink-0 gap-1.5">
              {SCENES.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-label={s.caption.en}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? "w-5 bg-primary" : "w-1.5 bg-primary/25"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
